import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiMail,
  FiPhone,
  FiMapPin,
  FiTwitter,
  FiLinkedin,
  FiGithub,
  FiArrowUp,
  FiExternalLink
} = FiIcons;

const CEO_PARTNER_URL = 'https://ceo-wanted.appscamelot.com/';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    Services: [
      'Web Development',
      'Mobile Apps',
      'API Development',
      'Cloud Deployment',
      'UI/UX Design',
      'Consulting'
    ],
    Company: [
      'About Us',
      'Our Process',
      'Case Studies',
      'Careers',
      'Blog',
      'Contact'
    ],
    Resources: [
      'MVP Guide',
      'Tech Stack',
      'Pricing',
      'FAQ',
      'Documentation',
      'Support'
    ]
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="text-xl font-bold">MVP72</span>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                We specialize in rapid MVP development, helping startups and businesses bring
                their ideas to life in 72 hours or less. Our experienced team delivers
                high-quality, scalable solutions.
              </p>
              <a
                href={CEO_PARTNER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mb-7 px-4 py-3 rounded-lg border border-purple-400/40 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20 hover:text-white transition-colors"
              >
                Looking for a CEO Partner
                <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-2" />
              </a>
              <div className="space-y-3">
                <div className="flex items-center text-gray-300">
                  <SafeIcon icon={FiMail} className="w-4 h-4 mr-3" />
                  hello@mvp72.com
                </div>
                <div className="flex items-center text-gray-300">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 mr-3" />
                  +1 (555) 123-4567
                </div>
                <div className="flex items-center text-gray-300">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-3" />
                  San Francisco, CA
                </div>
              </div>
            </motion.div>
          </div>

          {Object.entries(footerLinks).map(([category, links], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="font-semibold text-white mb-4">{category}</h3>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      className="text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-gray-400 text-sm mb-4 md:mb-0"
            >
              © 2024 MVP72. All rights reserved.
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="flex items-center space-x-6"
            >
              <div className="flex space-x-4">
                <a
                  href="#"
                  aria-label="Twitter"
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors duration-200"
                >
                  <SafeIcon icon={FiTwitter} className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  aria-label="LinkedIn"
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors duration-200"
                >
                  <SafeIcon icon={FiLinkedin} className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  aria-label="GitHub"
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-600 transition-colors duration-200"
                >
                  <SafeIcon icon={FiGithub} className="w-4 h-4" />
                </a>
              </div>
              <button
                type="button"
                aria-label="Back to top"
                onClick={scrollToTop}
                className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors duration-200"
              >
                <SafeIcon icon={FiArrowUp} className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;